import {
  ACTION_TYPE_TO_CASE,
  CASE_TYPE_CONFIG,
  CASE_TYPES,
  DEFAULT_GUARD_RAILS,
  MATCH_TYPE_KEYS,
} from "./constants";
import {
  buildCubeFiltersFromFilterModule,
  filterModuleHasContent,
  hourValuesInclusive,
  hydrateFilterModuleFromCubeFilters,
  injectHourOfDayDimension,
  cubeFiltersHaveUserRules,
} from "./cubeFilters";
import { timePeriodToCustomDate } from "../../../helper/Quadrant/quadrantHelper";
import {
  formatIsoDateForInput,
  frequencyValuesFromDayIndices,
  generateId,
  getMinCampaignBudget,
  parseNumOrEmpty,
  selectedDayIndicesFromFrequency,
} from "./helpers";

export function createDefaultCampaignBidCaps() {
  return {
    status: "enabled",
    min_value: getMinCampaignBudget(),
    max_value: "",
  };
}

export function createDefaultMatchTypeBids() {
  return {
    enabled: false,
    exact: { enabled: false, value: "" },
    phrase: { enabled: false, value: "" },
    broad: { enabled: false, value: "" },
  };
}

export function createDefaultPerfFilter() {
  return {
    enabled: false,
    timePeriod: "LAST_7_DAY",
    customDate: "last_7d",
    cubeFilters: "",
    filterModuleFilters: null,
  };
}

export function createDefaultCase(caseType = CASE_TYPES.INCREASE) {
  return {
    localId: generateId(),
    caseType,
    percentageChange: caseType === CASE_TYPES.PAUSE ? null : "",
    matchTypeBids: createDefaultMatchTypeBids(),
    perfFilter: createDefaultPerfFilter(),
  };
}

export function createDefaultSlotCases() {
  return {
    [CASE_TYPES.INCREASE]: [createDefaultCase(CASE_TYPES.INCREASE)],
    [CASE_TYPES.DECREASE]: [],
    [CASE_TYPES.PAUSE]: [],
  };
}

export function createDefaultSlot() {
  return {
    localId: generateId(),
    durationId: `dur-${generateId()}`,
    status: "active",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    activeCaseTab: CASE_TYPES.INCREASE,
    cases: createDefaultSlotCases(),
  };
}

export function createInitialFormState() {
  return {
    ruleName: "",
    status: "Active",
    activeCampaignFilter: "All",
    selectedCampaignIds: [],
    campaignMeta: {},
    campaignTableBids: {},
    automationMode: "ALL_DAYS_CUSTOM_DAYS",
    scheduleStartDate: "",
    scheduleEndDate: "",
    selectedDays: [0, 1, 2, 3, 4, 5, 6],
    gapHandling: "USE_DEFAULT_BID",
    targetingExclusion: {
      enabled: false,
      automatic: false,
      broad: false,
    },
    slots: [createDefaultSlot()],
  };
}

/** Reset slot/day fields when switching between All Days and Dayparting. */
export function buildAutomationModeResetPatch(_prevForm, automationMode) {
  return {
    automationMode,
    selectedDays: [0, 1, 2, 3, 4, 5, 6],
    slots: [createDefaultSlot()],
  };
}

function buildScheduleConfigForForm(form) {
  const freqValues = frequencyValuesFromDayIndices(form.selectedDays);

  return {
    type: "TargetingSchedule",
    day_frequency: "CUSTOM_DAYS",
    frequency_values:
      freqValues ??
      ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"],
    start_hour: null,
    end_hour: null,
    start_date: form.scheduleStartDate || null,
    end_date: form.scheduleEndDate || null,
    timezone: null,
  };
}

function buildDurationConfigForSlot(form, slot) {
  const isDayparting = form.automationMode === "DAYPARTING";

  if (isDayparting) {
    return {
      status: slot.status || "active",
      start_date: form.scheduleStartDate || null,
      end_date: form.scheduleEndDate || null,
      start_time: slot.startTime || "09:00",
      end_time: slot.endTime || "18:00",
    };
  }

  return {
    status: slot.status || "active",
    start_date: slot.startDate || form.scheduleStartDate || null,
    end_date: slot.endDate || form.scheduleEndDate || null,
    start_time: null,
    end_time: null,
  };
}

function buildEntityMetricFilter(caseItem, platformIds, userPlatform, hourValues = []) {
  const perf = caseItem?.perfFilter;
  const fromModule = filterModuleHasContent(perf?.filterModuleFilters)
    ? buildCubeFiltersFromFilterModule(perf.filterModuleFilters, {
        timePeriod: perf?.timePeriod,
        platformCampaignIds: platformIds,
        userPlatform,
      })
    : "";
  const storedCube = cubeFiltersHaveUserRules(perf?.cubeFilters) ? perf.cubeFilters.trim() : "";
  let cube = fromModule || storedCube;

  if (hourValues.length) {
    cube = injectHourOfDayDimension(cube, hourValues);
  }

  return {
    type: "CUBE_BASED_NESTED_AND_OR",
    time_period: perf?.timePeriod || "LAST_7_DAY",
    cube_filters: cube || "",
  };
}

function buildMatchTypeOverrides(caseItem) {
  const mt = caseItem?.matchTypeBids;
  if (!mt?.enabled) return null;

  const out = {};
  MATCH_TYPE_KEYS.forEach((key) => {
    const row = mt[key];
    if (!row?.enabled) return;
    const val = parseNumOrEmpty(row.value);
    if (val === "") return;
    out[key] = { percentage_change: Number(val) };
  });
  return Object.keys(out).length ? out : null;
}

/** Caps only — same action applies to all selected campaigns. */
function buildEntityConfigsForCaps(form) {
  return form.selectedCampaignIds.map((mongoId) => {
    const table = form.campaignTableBids[mongoId] ?? {};
    const minVal = parseNumOrEmpty(table.min_value);
    const maxVal = parseNumOrEmpty(table.max_value);

    return {
      entity_type: "campaign",
      id: mongoId,
      value_constraints: {
        metric: "BID",
        ...(minVal !== "" && { min_value: Number(minVal) }),
        ...(maxVal !== "" && { max_value: Number(maxVal) }),
      },
      change_config: null,
      child_entities: null,
    };
  });
}

function buildEntityFilter(form) {
  const filters = [
    {
      field_name: "Campaign_Id",
      operator: "IN",
      value: [...form.selectedCampaignIds],
    },
  ];

  const exclusion = form.targetingExclusion;
  if (exclusion?.enabled) {
    const excludeValues = [];
    if (exclusion.automatic) excludeValues.push("auto");
    if (exclusion.broad) excludeValues.push("broad");
    if (excludeValues.length) {
      filters.push({
        field_name: "Targeting_Type",
        operator: "NOT_CONTAINS",
        value: excludeValues,
      });
    }
  }

  return {
    version: "targeting_V2",
    entity_filter_rule: { filters },
  };
}

function caseActionType(caseType) {
  return CASE_TYPE_CONFIG[caseType]?.actionType ?? "INCREASE_BID";
}

function flattenSlotCases(slot) {
  const out = [];
  Object.values(CASE_TYPES).forEach((caseType) => {
    (slot.cases?.[caseType] ?? []).forEach((caseItem, index) => {
      out.push({ caseType, caseItem, caseIndex: index });
    });
  });
  return out;
}

export function formToApiRule(form, { clientId, email, ruleId, createdAt, userPlatform = "amazon" }) {
  const now = new Date().toISOString();
  const name = form.ruleName.trim();
  const platformIds = form.selectedCampaignIds
    .map((mid) => form.campaignMeta[mid]?.platformId)
    .filter(Boolean);

  const schedule_config = buildScheduleConfigForForm(form);
  const entity_configs = buildEntityConfigsForCaps(form);
  const rule_tasks = [];

  form.slots.forEach((slot, slotIndex) => {
    const duration = buildDurationConfigForSlot(form, slot);
    const hourValues =
      form.automationMode === "DAYPARTING"
        ? hourValuesInclusive(slot.startTime, slot.endTime)
        : [];
    flattenSlotCases(slot).forEach(({ caseType, caseItem, caseIndex }) => {
      const actionType = caseActionType(caseType);
      const isPause = caseType === CASE_TYPES.PAUSE;
      const pct = parseNumOrEmpty(caseItem.percentageChange);

      rule_tasks.push({
        rule_task_name: `Duration ${slotIndex + 1} - ${CASE_TYPE_CONFIG[caseType].label} ${caseIndex + 1}`,
        entity_metric_filter: buildEntityMetricFilter(
          caseItem,
          platformIds,
          userPlatform,
          hourValues
        ),
        entity_configs,
        rule_action_config: {
          entity_action_config: {
            action_type: actionType,
            apply_decision_flag: true,
            percentage_change: isPause ? null : pct === "" ? 0 : Number(pct),
            threshold: null,
            entity_lock_duration_days: null,
            entity_type_wise_percentage_change_and_threshold: isPause
              ? null
              : buildMatchTypeOverrides(caseItem),
            duration_config: { ...duration },
          },
          max_percent_exclude: 50,
        },
      });
    });
  });

  return {
    ...(ruleId ? { id: ruleId } : {}),
    status: form.status || "Active",
    client_id: clientId,
    entity_filter: buildEntityFilter(form),
    rule_tasks,
    schedule_config,
    name,
    type: "AMAZON_BID_OPTIMIZATION_V2",
    modified_by: email || "local@user",
    apply_decision: null,
    source: userPlatform || "amazon",
    created_at: createdAt || now,
    updated_at: now,
    rule_objective: "Bid_Optimization_V2",
    rule_config: {
      automation_mode: form.automationMode,
      gap_handling: form.gapHandling,
      targeting_exclusion: {
        enabled: Boolean(form.targetingExclusion?.enabled),
        exclude: [
          ...(form.targetingExclusion?.automatic ? ["auto"] : []),
          ...(form.targetingExclusion?.broad ? ["broad"] : []),
        ],
      },
      guardrails: { ...DEFAULT_GUARD_RAILS },
    },
  };
}

/** UI must never send duration_config.id (create or update). */
function stripDurationConfigIds(ruleTasks = []) {
  return ruleTasks.map((task) => {
    const duration =
      task?.rule_action_config?.entity_action_config?.duration_config;
    if (!duration || !Object.prototype.hasOwnProperty.call(duration, "id")) {
      return task;
    }

    const { id: _durationId, ...durationWithoutId } = duration;
    return {
      ...task,
      rule_action_config: {
        ...task.rule_action_config,
        entity_action_config: {
          ...task.rule_action_config.entity_action_config,
          duration_config: durationWithoutId,
        },
      },
    };
  });
}

/** Request body for POST/PATCH automation-rule-set. Rule id is sent only on update. */
export function formToApiRequestBody(form, { email, ruleId, createdAt, clientId, userPlatform }) {
  const rule = formToApiRule(form, { clientId, email, ruleId, createdAt, userPlatform });
  const isCreate = !ruleId;

  const body = {
    type: rule.type,
    name: rule.name,
    status: rule.status,
    rule_objective: rule.rule_objective,
    source: rule.source,
    modified_by: rule.modified_by,
    client_id: rule.client_id,
    apply_decision: rule.apply_decision,
    entity_filter: rule.entity_filter,
    rule_config: rule.rule_config,
    schedule_config: rule.schedule_config,
    rule_tasks: stripDurationConfigIds(rule.rule_tasks),
  };

  if (!isCreate && rule.id) {
    body.id = rule.id;
  }

  return body;
}

function durationKey(dc = {}) {
  return [
    formatIsoDateForInput(dc.start_date),
    formatIsoDateForInput(dc.end_date),
    String(dc.start_time ?? "").slice(0, 5),
    String(dc.end_time ?? "").slice(0, 5),
    dc.status || "active",
  ].join("|");
}

function hydrateMatchTypeBids(matchOverrides) {
  const base = createDefaultMatchTypeBids();
  if (!matchOverrides || typeof matchOverrides !== "object") return base;

  let any = false;
  MATCH_TYPE_KEYS.forEach((key) => {
    const pct = matchOverrides[key]?.percentage_change;
    if (pct === "" || pct == null) return;
    any = true;
    base[key] = { enabled: true, value: pct };
  });
  base.enabled = any;
  return base;
}

export function apiRuleToForm(rule) {
  if (!rule) return createInitialFormState();

  const form = createInitialFormState();
  form.ruleName = rule.name ?? "";
  form.status = rule.status ?? "Active";
  form.automationMode =
    rule.rule_config?.automation_mode ??
    (rule.rule_tasks?.[0]?.rule_action_config?.entity_action_config?.duration_config?.start_time
      ? "DAYPARTING"
      : "ALL_DAYS_CUSTOM_DAYS");
  form.gapHandling = rule.rule_config?.gap_handling ?? "USE_DEFAULT_BID";
  form.scheduleStartDate = formatIsoDateForInput(rule.schedule_config?.start_date);
  form.scheduleEndDate = formatIsoDateForInput(rule.schedule_config?.end_date);
  form.selectedDays = selectedDayIndicesFromFrequency(rule.schedule_config?.frequency_values);

  const exclusionCfg = rule.rule_config?.targeting_exclusion;
  const entityFilters = rule.entity_filter?.entity_filter_rule?.filters ?? [];
  const targetingTypeFilter = entityFilters.find((f) => f.field_name === "Targeting_Type");
  const excludeValues = exclusionCfg?.exclude ?? targetingTypeFilter?.value ?? [];
  form.targetingExclusion = {
    enabled: Boolean(exclusionCfg?.enabled ?? excludeValues.length),
    automatic: excludeValues.includes("auto"),
    broad: excludeValues.includes("broad"),
  };

  const campaignIds = [];
  const campaignMeta = {};
  const campaignTableBids = {};
  const slotsByKey = new Map();

  (rule.rule_tasks ?? []).forEach((task) => {
    const actionCfg = task.rule_action_config?.entity_action_config ?? {};
    const dc = actionCfg.duration_config ?? {};
    const key = durationKey(dc);

    if (!slotsByKey.has(key)) {
      const slot = createDefaultSlot();
      slot.cases = {
        [CASE_TYPES.INCREASE]: [],
        [CASE_TYPES.DECREASE]: [],
        [CASE_TYPES.PAUSE]: [],
      };
      slot.durationId = dc.id ?? slot.durationId;
      slot.status = dc.status ?? "active";
      slot.startDate = formatIsoDateForInput(dc.start_date);
      slot.endDate = formatIsoDateForInput(dc.end_date);
      slot.startTime = String(dc.start_time ?? "09:00").slice(0, 5);
      slot.endTime = String(dc.end_time ?? "18:00").slice(0, 5);
      slotsByKey.set(key, slot);
    }

    const slot = slotsByKey.get(key);
    const caseType =
      ACTION_TYPE_TO_CASE[actionCfg.action_type] ?? CASE_TYPES.INCREASE;
    const emf = task.entity_metric_filter;
    const caseItem = createDefaultCase(caseType);
    caseItem.percentageChange =
      caseType === CASE_TYPES.PAUSE
        ? null
        : actionCfg.percentage_change ?? "";
    caseItem.matchTypeBids = hydrateMatchTypeBids(
      actionCfg.entity_type_wise_percentage_change_and_threshold
    );

    if (emf) {
      const moduleFilters = hydrateFilterModuleFromCubeFilters(emf.cube_filters);
      const hasUserFilters = filterModuleHasContent(moduleFilters);
      caseItem.perfFilter.enabled = hasUserFilters;
      caseItem.perfFilter.timePeriod = emf.time_period ?? "LAST_7_DAY";
      caseItem.perfFilter.customDate = timePeriodToCustomDate(emf.time_period);
      caseItem.perfFilter.cubeFilters = hasUserFilters
        ? typeof emf.cube_filters === "string"
          ? emf.cube_filters
          : JSON.stringify(emf.cube_filters ?? "")
        : "";
      caseItem.perfFilter.filterModuleFilters = hasUserFilters ? moduleFilters : null;
    }

    slot.cases[caseType].push(caseItem);

    (task.entity_configs ?? []).forEach((ec) => {
      if (ec.entity_type !== "campaign") return;
      const mid = String(ec.id);
      if (!campaignIds.includes(mid)) campaignIds.push(mid);
      const vc = ec.value_constraints ?? {};
      campaignTableBids[mid] = {
        status: "enabled",
        min_value:
          vc.min_value !== "" && vc.min_value != null
            ? vc.min_value
            : getMinCampaignBudget(),
        max_value: vc.max_value ?? "",
      };
      campaignMeta[mid] = { ...(campaignMeta[mid] || {}) };
    });
  });

  const slots = [...slotsByKey.values()].map((slot) => {
    const hasIncrease = slot.cases.increase.length > 0;
    const hasDecrease = slot.cases.decrease.length > 0;
    if (!hasIncrease && !hasDecrease && slot.cases.pause.length === 0) {
      slot.cases.increase = [createDefaultCase(CASE_TYPES.INCREASE)];
    }
    slot.activeCaseTab = hasIncrease
      ? CASE_TYPES.INCREASE
      : hasDecrease
        ? CASE_TYPES.DECREASE
        : CASE_TYPES.PAUSE;
    return slot;
  });

  form.selectedCampaignIds = campaignIds;
  form.campaignTableBids = campaignTableBids;
  form.campaignMeta = campaignMeta;
  form.slots = slots.length ? slots : [createDefaultSlot()];

  return form;
}
